import {
  Activity,
  AlertCircle,
  ArrowDown,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { cn } from "@/lib/utils";
import {
  type ProjectActivityEntry,
  groupEntriesByDate,
} from "./activity-feed-types";
import { ActivityFeedEntry } from "./activity-feed-entry";

interface ActivityFeedListProps {
  entries: ProjectActivityEntry[];
  projectId: string;
  isLoading: boolean;
  isFiltering?: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  error?: unknown;
  onRetry?: () => void;
}

export function ActivityFeedList({
  entries,
  projectId,
  isLoading,
  isFiltering = false,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  hasActiveFilters,
  onResetFilters,
  error,
  onRetry,
}: ActivityFeedListProps) {
  if (error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Unable to load activity stream"
        description="There was a problem communicating with the server. Please check your network and retry."
        unframed
        actions={
          onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="size-3.5 mr-1.5" />
              Retry
            </Button>
          ) : undefined
        }
        className="py-12"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 py-4" aria-label="Loading activity feed">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-start gap-3 py-2">
            <Skeleton className="size-7 rounded-full shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    if (hasActiveFilters) {
      return (
        <EmptyState
          icon={Activity}
          title="No matching events found"
          description="None of the recorded events match your current search query or active filter settings."
          unframed
          actions={
            <Button variant="outline" size="sm" onClick={onResetFilters}>
              Clear all filters
            </Button>
          }
          className="py-12"
        />
      );
    }

    return (
      <EmptyState
        icon={Activity}
        title="No project activity yet"
        description="Every state change, assignment, CLI agent pull, and triage comment across this project will appear here in chronological order."
        unframed
        className="py-12"
      />
    );
  }

  const groups = groupEntriesByDate(entries);

  return (
    <div
      className={cn(
        "space-y-6 transition-opacity duration-150",
        isFiltering && "opacity-60 pointer-events-none",
      )}
    >
      {groups.map((group) => (
        <section
          key={group.label}
          aria-label={group.label}
          className="space-y-1"
        >
          <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-xs py-1.5 border-b border-border/40">
            <span className="text-2xs font-semibold text-muted-foreground">
              {group.label}
            </span>
          </div>

          <div className="divide-y divide-border/30">
            {group.entries.map((entry) => (
              <ActivityFeedEntry
                key={entry.id}
                entry={entry}
                projectId={projectId}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Pagination controls */}
      <div className="pt-4 flex flex-col items-center gap-2 border-t border-border/40">
        {hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
            className="text-xs"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                Loading older activity…
              </>
            ) : (
              <>
                <ArrowDown className="size-3.5 mr-1.5" />
                Load older activity
              </>
            )}
          </Button>
        ) : (
          <p className="text-2xs text-muted-foreground">
            End of project activity stream
          </p>
        )}
      </div>
    </div>
  );
}
